import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  isLocalDevAuthBypassEnabled,
  isSSOConfigured,
  loginRequest,
} from '@/config/msalConfig';
import { useAuth } from '@/contexts/AuthContext';
import LCIcon from '@/components/common/LCIcon';

interface LocationState {
  from?: { pathname: string };
}

// Too complex for a Tailwind arbitrary value (multi-stop radial gradients with commas)
const EDITORIAL_GLOW: React.CSSProperties = {
  background: [
    'radial-gradient(1100px 700px at 25% 45%, rgba(229, 0, 37, 0.09), transparent 60%)',
    'radial-gradient(800px 600px at 85% 90%, rgba(255, 255, 255, 0.04), transparent 60%)',
  ].join(', '),
};

const Login: React.FC = () => {
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [showPw, setShowPw]         = useState(false);
  const [showPwForm, setShowPwForm] = useState(false);
  const [error, setError]           = useState('');
  const [isLoading, setIsLoading]       = useState(false);
  const [isSSOLoading, setIsSSOLoading] = useState(false);

  const { isAuthenticated, isLoading: isAuthLoading, login, loginWithSSO } = useAuth();
  const { instance } = useMsal();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from = (location.state as LocationState)?.from?.pathname || '/';
  const busy = isLoading || isSSOLoading;
  const ssoAvailable = isSSOConfigured();

  // A direct visit to /login should not expose a form when the local-only
  // bypass has already established its development session.
  useEffect(() => {
    if (isLocalDevAuthBypassEnabled() && isAuthenticated && !isAuthLoading) {
      navigate(from, { replace: true });
    }
  }, [from, isAuthenticated, isAuthLoading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSSOLogin = async () => {
    setError('');
    setIsSSOLoading(true);
    try {
      const clientId = process.env.REACT_APP_AZURE_CLIENT_ID;
      const tenantId = process.env.REACT_APP_AZURE_TENANT_ID;
      if (!clientId || !tenantId) {
        throw new Error(
          'Microsoft SSO is not configured. Please set REACT_APP_AZURE_CLIENT_ID and REACT_APP_AZURE_TENANT_ID.'
        );
      }
      const response = await instance.loginPopup(loginRequest);
      if (response?.idToken) {
        await loginWithSSO(response.idToken);
        navigate(from, { replace: true });
      } else {
        throw new Error('No ID token received from Microsoft');
      }
    } catch (err) {
      let msg = 'SSO login failed. Please try again.';
      if (err instanceof Error) {
        msg = err.message.includes('AADSTS900144') || err.message.includes('client_id')
          ? 'Azure AD configuration error: Missing client_id. Please configure REACT_APP_AZURE_CLIENT_ID.'
          : err.message;
      }
      setError(msg);
    } finally {
      setIsSSOLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-[2fr_1fr] min-h-screen max-[1400px]:grid-cols-[3fr_2fr] max-[1100px]:grid-cols-1">

      {/* ── Left: dark editorial hero panel ───────────────────── */}
      <section className="relative bg-[#0f1419] text-white flex flex-col overflow-hidden pt-[56px] px-[80px] pb-[48px] max-[1400px]:px-[48px] max-[1400px]:pt-[36px] max-[1400px]:pb-[32px] max-[1100px]:hidden">

        {/* Radial gradient glow */}
        <div className="absolute inset-0 pointer-events-none" style={EDITORIAL_GLOW} />

        {/* SVG grid pattern */}
        <svg
          className="absolute inset-0 w-full h-full opacity-[0.05] pointer-events-none"
          aria-hidden="true"
        >
          <defs>
            <pattern id="login-editorial-grid" width="88" height="88" patternUnits="userSpaceOnUse">
              <path d="M88 0H0V88" fill="none" stroke="#ffffff" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#login-editorial-grid)" />
        </svg>

        {/* Brand mark */}
        <div className="relative z-10 flex items-center gap-[14px]">
          <div className="w-[44px] h-[44px] bg-[#E50025] shrink-0 grid place-items-center text-white font-serif text-[24px] font-medium leading-none max-[1400px]:w-[34px] max-[1400px]:h-[34px] max-[1400px]:text-[18px]">
            L
          </div>
          <span className="font-serif text-[19px] leading-[1.1] text-white max-[1400px]:text-[15px]">
            Lighthouse Canton
          </span>
        </div>

        {/* Vertically centred hero */}
        <div className="relative z-10 flex-1 flex flex-col justify-center min-h-0 max-w-[720px]">
          <div className="font-serif font-normal text-[26px] leading-[1.2] tracking-[-0.005em] text-white/90 mb-[18px] max-[1400px]:text-[17px] max-[1400px]:mb-[12px]">
            Margin Risk Management System
          </div>

          <div className="w-[56px] h-[2px] bg-[#E50025] mb-[26px] max-[1400px]:mb-[16px]" />

          <h1 className="font-serif font-normal text-[60px] leading-[1.06] tracking-[-0.018em] text-white text-balance m-0 max-[1400px]:text-[34px]">
            Margin Risk &mdash;{' '}
            <em className="not-italic text-white/[72]">
              tracked, measured, and controlled
            </em>{' '}
            against limits, every day.
          </h1>

          <p className="text-[17px] leading-[1.6] text-white/60 mt-[32px] max-w-[580px] m-0 max-[1400px]:text-[13px] max-[1400px]:mt-[16px]">
            Real-time monitoring of margin requirements, surplus cushions, and house-call triggers
            across every Pershing-held portfolio. Cross-book exposure analysis, RM-actionable alerts,
            and a full audit trail of margin events.
          </p>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex justify-between items-center mt-[40px] pt-[24px] border-t border-white/[0.08] max-[1400px]:mt-[20px] max-[1400px]:pt-[16px]">
          <div className="flex items-center gap-[14px]">
            <span className="text-[10.5px] font-bold tracking-[0.18em] uppercase text-white/40">
              Regulated by
            </span>
            {['MAS', 'DFSA', 'SEBI', 'FCA'].map(r => (
              <span
                key={r}
                className="text-[11px] font-bold tracking-[0.22em] text-white/55 px-[10px] py-[5px] border border-white/[0.14]"
              >
                {r}
              </span>
            ))}
          </div>

          <div className="flex flex-col items-end gap-[3px]">
            <em className="font-serif not-italic text-[11.5px] text-white/[42]">
              A product of AI Lab
            </em>
            <span className="text-[11px] text-white/30 font-mono tracking-[0.04em]">
              MRMS &middot; {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>
      </section>

      {/* ── Right: sign-in panel ───────────────────────────────── */}
      <section className="flex flex-col bg-[#f5f3ee] pt-[48px] px-[80px] pb-[36px] border-l border-black/[0.08] overflow-y-auto max-[1400px]:pt-[32px] max-[1400px]:px-[56px] max-[1400px]:pb-[24px] max-[1100px]:px-8 max-[1100px]:py-9">

        <div className="flex-1 flex flex-col justify-center mx-auto w-full max-w-[440px]">

          {/* Error banner */}
          {error && (
            <div className="flex items-start gap-2.5 px-[12px] py-[10px] mb-5 text-[12.5px] leading-relaxed border-l-[3px] border-[#c62828] bg-[#fdecec] text-[#8a1b1b]">
              <LCIcon name="error" size={14} className="shrink-0 mt-0.5" />
              <span className="flex-1">{error}</span>
              <button
                className="bg-transparent border-0 cursor-pointer p-0 text-inherit opacity-60 hover:opacity-100 shrink-0"
                onClick={() => setError('')}
              >
                <LCIcon name="close" size={12} />
              </button>
            </div>
          )}

          {/* Eyebrow */}
          <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-[#E50025] m-0 mb-[14px]">
            Secure access &middot; Single sign-on
          </p>

          {/* Title */}
          <h2 className="font-serif font-normal text-[52px] leading-[1.02] tracking-[-0.018em] text-black m-0 mb-[16px] max-[1400px]:text-[40px]">
            Sign in
          </h2>

          {/* Lead */}
          {ssoAvailable && !showPwForm && (
            <p className="text-[15px] leading-[1.55] text-[#7A7A76] max-w-[380px] m-0 mb-[38px] max-[1400px]:mb-[24px]">
              Use your Lighthouse Canton Microsoft account. MRMS inherits your firm role
              and margin-account access automatically.
            </p>
          )}

          {/* SSO button */}
          {ssoAvailable && !showPwForm && (
            <>
              <button
                onClick={handleSSOLogin}
                disabled={busy}
                className="flex items-center justify-center gap-[14px] w-full px-[20px] py-[16px] bg-[#E50025] text-white text-[16px] font-medium tracking-[-0.01em] border-0 cursor-pointer transition-colors hover:bg-[#B80020] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg width="19" height="19" viewBox="0 0 23 23" aria-hidden="true" className="shrink-0">
                  <rect x="1"  y="1"  width="10" height="10" fill="#F25022" />
                  <rect x="12" y="1"  width="10" height="10" fill="#7FBA00" />
                  <rect x="1"  y="12" width="10" height="10" fill="#00A4EF" />
                  <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
                </svg>
                <span className="flex-1 text-left">
                  {isSSOLoading ? 'Signing in…' : 'Continue with LC Account'}
                </span>
                <svg
                  width="16" height="16" viewBox="0 0 14 14" fill="none"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                  className="ml-[4px] shrink-0"
                >
                  <path d="M3 7h8M7 3l4 4-4 4" />
                </svg>
              </button>

              <p className="text-[12px] text-[#7A7A76] mt-[18px] leading-[1.5] m-0">
                Sign in with your{' '}
                <strong className="text-black">@lighthouse-canton.com</strong>{' '}
                or{' '}
                <strong className="text-black">@keenaiglobal.com</strong>{' '}
                account.
              </p>

              <div className="mt-[20px] pt-[20px] border-t border-black/[0.08]">
                <button
                  className="text-[11.5px] text-[#7A7A76] hover:text-[#333] bg-transparent border-0 p-0 cursor-pointer transition-colors"
                  onClick={() => setShowPwForm(true)}
                >
                  Use email / password instead &rarr;
                </button>
              </div>
            </>
          )}

          {/* Email / password form */}
          {(!ssoAvailable || showPwForm) && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-1">
              {showPwForm && (
                <p className="text-[13px] leading-[1.65] text-[#7A7A76] m-0">
                  Sign in with your MRMS account credentials.
                </p>
              )}

              <div className="flex flex-col gap-1.5">
                <label
                  className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#333]"
                  htmlFor="login-email"
                >
                  Email
                </label>
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  disabled={busy}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@lighthouse-canton.com"
                  className="w-full text-[13.5px] font-light px-[12px] py-[10px] rounded-none bg-white text-black border border-black/[0.16] focus:outline-none focus:border-[#E50025] disabled:opacity-50 transition-colors"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  className="text-[10px] tracking-[0.16em] uppercase font-bold text-[#333]"
                  htmlFor="login-password"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPw ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    disabled={busy}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full text-[13.5px] font-light pl-[12px] pr-[42px] py-[10px] rounded-none bg-white text-black border border-black/[0.16] focus:outline-none focus:border-[#E50025] disabled:opacity-50 transition-colors"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-0 p-0 cursor-pointer text-[#7A7A76] hover:text-[#333] transition-colors"
                    onClick={() => setShowPw(v => !v)}
                  >
                    <LCIcon name={showPw ? 'eye-off' : 'eye'} size={15} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={busy}
                className="flex items-center justify-center w-full px-[20px] py-[14px] bg-[#E50025] text-white text-[14px] font-medium border-0 cursor-pointer hover:bg-[#B80020] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-1"
              >
                {isLoading ? 'Signing in…' : 'Sign In'}
              </button>

              {showPwForm && ssoAvailable && (
                <button
                  type="button"
                  className="text-[11.5px] text-[#7A7A76] hover:text-[#333] bg-transparent border-0 p-0 cursor-pointer transition-colors text-left"
                  onClick={() => { setShowPwForm(false); setError(''); }}
                >
                  &larr; Back to SSO sign-in
                </button>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-[24px] border-t border-black/[0.08] text-[12px] text-[#7A7A76]">
          <span>
            Trouble signing in?{' '}
            <a
              href="mailto:risk.lcpl@lighthouse-canton.com"
              className="text-[#E50025] font-semibold border-b border-[#E50025] pb-[1px]"
            >
              Risk &amp; CPL Support
            </a>
          </span>
          <span className="font-mono">
            &copy; {new Date().getFullYear()} Lighthouse Canton
          </span>
        </div>
      </section>
    </div>
  );
};

export default Login;
